import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Check, Phone, Star, HelpCircle, Cloud, RefreshCw, GraduationCap, Zap, LineChart, LifeBuoy } from "lucide-react";
import { publicApi } from "../lib/api";
import { getLocation, LOCATIONS } from "../data/locations";
import { getXeroLocation, XERO_LOCATIONS } from "../data/xeroLocations";
import { getCountyCopy } from "../data/countyCopy";
import { OPENING_HOURS_SPEC } from "../lib/business";
import Seo, { breadcrumbSchema, faqSchema, logoUrl } from "../components/Seo";
import NotFound from "./NotFound";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import BookCallButton from "../components/BookCallButton";
import XeroSavings from "../components/XeroSavings";
import SupportCaveat from "../components/SupportCaveat";

const siteOrigin = () => (typeof window !== "undefined" ? window.location.origin : "");

const XERO_SERVICES = [
  [Cloud, "Xero setup", "We configure Xero around your business from day one — chart of accounts, bank feeds, invoicing and more."],
  [RefreshCw, "Xero migration", "Switching from Sage, QuickBooks or spreadsheets? We move your data across cleanly, with no disruption."],
  [GraduationCap, "Xero training", "Hands-on training so you and your team feel genuinely confident using Xero day to day."],
  [Zap, "App integrations", "We connect the right add-ons — expenses, payments, forecasting — so Xero does more with less admin."],
  [LineChart, "Real-time reporting", "Live dashboards and financial reports, so you always know your cash flow and profit."],
  [LifeBuoy, "Ongoing support", "Unlimited Xero support on a fixed monthly fee with an active support package — a dedicated contact whenever a question comes up."],
];

// Shared Xero FAQs appended after the town-specific ones.
const SHARED_XERO_FAQS = [
  {
    question: "What does being a Xero Platinum Partner mean?",
    answer:
      "Platinum is Xero's highest partner status, reflecting deep experience and a large base of clients we support on Xero. In practice it means genuine expertise, priority support and getting the very best out of the platform for your business.",
  },
  {
    question: "How much do your Xero accounting services cost?",
    answer:
      "Our pricing is published, so you don't have to ask. Build your package on our pricing page — Xero subscription, bookkeeping, VAT, payroll and advice as required — and you'll see your own fixed monthly fee and any one-off costs in about two minutes, with no email address needed.",
  },
  {
    question: "How much is the Xero subscription itself?",
    answer:
      "Xero's UK plans run from £7 to £70 a month plus VAT, and as a Xero Platinum Partner we can supply the same plans for 20% less than Xero's list price — so Grow is £31.20 rather than £39. See every plan and both prices on our Xero Subscription page.",
  },
  {
    question: "Is Xero support included with the subscription?",
    answer:
      "Software support is subject to an active paid support package on a rolling monthly subscription and is available only for apps paid for through CASS. The subscription itself gets you the software at the partner price; the support package is what gets you a named person to ask.",
  },
];

export default function XeroLocationPage({ slug }) {
  const xero = getXeroLocation(slug);
  const loc = xero ? getLocation(xero.baseSlug) : null;
  const [rating, setRating] = useState(null);
  const [testimonial, setTestimonial] = useState(null);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    publicApi.googleRating().then(setRating).catch(() => {});
    publicApi.xeroPricing().then(setPricing).catch(() => setPricing(null));
    if (xero) {
      publicApi.townTestimonials()
        .then((all) => setTestimonial((all || {})[xero.baseSlug] || null))
        .catch(() => setTestimonial(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!xero || !loc) return <NotFound />;

  const county = getCountyCopy(loc.county);
  const faqs = [...(xero.faqs || []), ...SHARED_XERO_FAQS];
  const otherXero = (() => {
    const i = Math.max(0, XERO_LOCATIONS.findIndex((x) => x.slug === slug));
    // Circular order starting after this page. The old same-county-first slice
    // always picked the same early towns, leaving Swadlincote and Wolverhampton
    // with a single internal inlink each. Taking the next three towns in the ring
    // guarantees every Xero page is linked from at least three others, then the
    // same-county towns fill the rest so the block still reads as local.
    const ring = [...XERO_LOCATIONS.slice(i + 1), ...XERO_LOCATIONS.slice(0, i)];
    const sameCounty = ring.filter((x) => getLocation(x.baseSlug)?.county === loc.county);
    const picked = [];
    for (const x of [...ring.slice(0, 3), ...sameCounty, ...ring]) {
      if (picked.length < 6 && !picked.includes(x)) picked.push(x);
    }
    return picked;
  })();

  const schema = {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    name: `Xero Accountants in ${loc.town} — Cloud Accounting Support Services`,
    description: xero.metaDescription,
    url: `${siteOrigin()}/${xero.slug}`,
    logo: logoUrl(),
    telephone: "+44 1827 780550",
    email: "info@cass-online.co.uk",
    areaServed: [loc.town, ...loc.nearby],
    knowsAbout: ["Xero", "Cloud accounting", "Bookkeeping", "VAT", "Payroll"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "3 Pebble Close, Amington",
      addressLocality: "Tamworth",
      addressRegion: "Staffordshire",
      postalCode: "B77 4RD",
      addressCountry: "GB",
    },
    openingHoursSpecification: OPENING_HOURS_SPEC,
    // Reviews of CASS itself are intentionally NOT marked up here (visible Google
    // reviews remain on the page). This avoids self-serving review-snippet markup.
  };

  // One designated page carries the regional query (see xeroLocations.js).
  const headline = xero.region ? `Xero Accountants in ${loc.town} & ${xero.region}` : `Xero Accountants in ${loc.town}`;

  return (
    <div className="bg-white" data-testid={`xero-location-page-${xero.slug}`}>
      <Seo title={headline} description={xero.metaDescription}
        jsonLd={[
          schema,
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: headline, path: `/${xero.slug}` }]),
          faqSchema(faqs),
        ]} />

      {/* Hero */}
      <section className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase mb-4">
            <Cloud size={16} /> Xero Platinum Partner · {loc.town}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">{headline}</h1>
          {rating?.rating && (
            <a href={rating.url || rating.write_review_url || "#"} target="_blank" rel="noopener noreferrer"
              data-testid="xero-rating-snippet"
              className="inline-flex items-center gap-2 mt-5 bg-white/5 border border-white/10 rounded-full pl-2.5 pr-4 py-1.5 hover:bg-white/10 transition-colors">
              <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} className={i <= Math.round(rating.rating) ? "fill-orange text-orange" : "fill-white/20 text-white/20"} />)}</span>
              <span className="text-sm font-semibold text-white">{rating.rating}/5</span>
              <span className="text-sm text-slate-300">· {rating.total} Google reviews</span>
            </a>
          )}
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">{xero.intro[0]}</p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/contact" data-testid="xero-cta-talk"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <BookCallButton testId="xero-book-call" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white rounded-full px-8 py-[1.15rem] text-base font-bold transition-all bg-transparent" />
          </div>
        </div>
      </section>

      {/* Intro continued */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">{xero.intro[1]}</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-8 max-w-2xl">
          {["Xero Platinum Partner", "Clean, disruption-free migration", "In-person & remote training", "Unlimited support, fixed monthly fees"].map((d) => (
            <div key={d} className="flex items-center gap-2.5 text-navy font-semibold"><span className="w-6 h-6 rounded-full bg-orange/10 flex items-center justify-center text-orange shrink-0"><Check size={14} /></span>{d}</div>
          ))}
        </div>
      </section>

      {/* Xero services */}
      <section className="bg-slate-50 py-16 md:py-20" data-testid="xero-services">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-orange">What we do</span>
          <h2 className="text-3xl font-extrabold text-navy mt-1">Xero services for {loc.town} businesses</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {XERO_SERVICES.map(([Icon, title, desc]) => (
              <div key={title} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-navy/5 text-navy flex items-center justify-center mb-4"><Icon size={20} /></div>
                <h3 className="font-display font-bold text-navy">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-2">{desc}</p>
              </div>
            ))}
          </div>
          {pricing?.support_caveat && <SupportCaveat text={pricing.support_caveat} className="mt-6 max-w-3xl" />}
        </div>
      </section>

      {/* What the subscription would cost through CASS */}
      {pricing?.enabled !== false && pricing && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="xero-town-savings">
          <span className="text-xs font-bold uppercase tracking-widest text-orange">Xero subscriptions</span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-navy mt-1">
            Pay less for the same Xero in {loc.town}
          </h2>
          <p className="text-slate-600 leading-relaxed mt-3 max-w-3xl">
            As a Xero Platinum Partner we supply the same plans for {pricing.cass_discount_pct || 20}% less than Xero's
            list price, whether or not we do your accounts.{" "}
            <Link to="/services/xero-subscription" className="text-orange font-semibold hover:underline" data-testid="xero-town-pricing-link">
              See every plan and price
            </Link>
            .
          </p>
          <XeroSavings cfg={pricing} town={loc.town} className="mt-8" />
        </section>
      )}

      {/* Why local (per county) */}
      {county && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="xero-county-copy">
          <h2 className="text-2xl md:text-3xl font-extrabold text-navy">{county.heading}</h2>
          <p className="text-slate-600 leading-relaxed mt-4 max-w-3xl">{county.body}</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {county.points.map((p) => (
              <div key={p} className="flex items-start gap-2.5 bg-slate-50 rounded-xl border border-slate-100 p-4"><Check size={18} className="text-orange shrink-0 mt-0.5" /><span className="text-sm font-medium text-navy leading-snug">{p}</span></div>
            ))}
          </div>
        </section>
      )}

      <GoogleReviewsSection testId="xero-reviews" eyebrow="Trusted locally"
        excludeAuthor={testimonial?.author || ""}
        offset={Math.max(0, XERO_LOCATIONS.findIndex((x) => x.slug === slug)) * 2 + 1} />

      {/* FAQs */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="xero-faqs">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-11 h-11 rounded-xl bg-orange/10 flex items-center justify-center text-orange shrink-0"><HelpCircle size={22} /></span>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-orange">Xero accountants in {loc.town}</span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-navy leading-tight">Frequently asked questions</h2>
          </div>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`xero-faq-${i}`} data-testid={`xero-faq-${i}`} className="bg-slate-50 rounded-xl border border-slate-100 px-5">
              <AccordionTrigger className="text-left font-display font-bold text-navy hover:no-underline">{f.question}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Cross-links */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-6 flex flex-col items-start gap-3">
        <Link to={`/${loc.slug}`} data-testid="xero-to-general" className="inline-flex items-center gap-2 text-orange font-bold hover:underline">
          <MapPin size={16} /> Looking for general accountancy in {loc.town}? See our {loc.town} accountants page <ArrowRight size={15} />
        </Link>
        <Link to="/xero-accountants" data-testid="xero-to-hub" className="inline-flex items-center gap-2 text-orange font-bold hover:underline">
          <Cloud size={16} /> Not in {loc.town}? See how we work with Xero UK-wide <ArrowRight size={15} />
        </Link>
      </section>

      {/* Related Xero towns */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="xero-related">
        <h2 className="text-2xl font-extrabold text-navy">Xero accountants in nearby towns</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
          {otherXero.map((x) => {
            const t = getLocation(x.baseSlug);
            return (
              <Link key={x.slug} to={`/${x.slug}`} data-testid={`xero-related-${x.slug}`}
                className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-orange hover:-translate-y-0.5 transition-all group">
                <span className="inline-flex items-center gap-2 font-semibold text-navy text-sm"><Cloud size={15} className="text-orange" /> Xero accountants in {t?.town}</span>
                <ArrowRight size={16} className="text-slate-300 group-hover:text-orange" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold">Ready to get more from Xero in {loc.town}?</h2>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Book a free, no-obligation chat. We'll review your set-up and show you exactly how we can help.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/contact"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <BookCallButton testId="xero-book-call-cta" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white rounded-full px-8 py-[1.15rem] text-base font-bold transition-all bg-transparent" />
            <a href="tel:+441827780550"><Button variant="outline" className="rounded-full px-8 py-6 text-base font-bold border-white/25 text-white hover:bg-white/10 bg-transparent"><Phone size={16} className="mr-2" /> 01827 780550</Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
